import { Button, Flex, Tooltip } from "antd";
import { CheckIcon, ClockIcon, FlagIcon, XIcon } from "@phosphor-icons/react";
import { useTranslate } from "@refinedev/core";
import { analyticsTheme } from "pages/media/components/analytics/analyticsTheme/";
import { colors } from "@libertify/ui/theme-v2";
import { InsightTaskState } from "../../types";
import { CSSProperties, useMemo } from "react";

export type InsightActionButtonsProps = {
  insightId: string;
  compact?: boolean;
  value?: InsightTaskState;
  onComplete?: (id: string) => void;
  onPostpone?: (id: string) => void;
  onDismiss?: (id: string) => void;
  onReport?: (id: string) => void;
};

export const InsightActionButtons = ({
  insightId,
  compact = true,
  value,
  onComplete,
  onPostpone,
  onDismiss,
  onReport,
}: InsightActionButtonsProps) => {
  const t = useTranslate();

  const actions = [
    {
      tooltipKey: "media.analytics.insights.markDone",
      icon: <CheckIcon size={compact ? 14 : 20} />,
      color: analyticsTheme.metricSuccess,
      handler: onComplete,
      label: t("media.analytics.insights.completed"),
      disabled: !onComplete,
      backgroundColor:
        value === "Done" ? analyticsTheme.metricSuccess : colors.status.successBg,
      checked: value === "Done",
    },
    {
      tooltipKey: "media.analytics.insights.postpone",
      icon: <ClockIcon size={compact ? 14 : 20} />,
      color: analyticsTheme.metricWarning,
      handler: onPostpone,
      label: t("media.analytics.insights.postponed"),
      disabled: !onPostpone,
      backgroundColor:
        value === "Snoozed" ? analyticsTheme.metricWarning : colors.status.warningBg,
      checked: value === "Snoozed",
    },
    {
      tooltipKey: "media.analytics.insights.dismiss",
      icon: <XIcon size={compact ? 14 : 20} />,
      color: analyticsTheme.metricError,
      handler: onDismiss,
      label: t("media.analytics.insights.dismissed"),
      disabled: !onDismiss,
      backgroundColor:
        value === "Dismissed" ? analyticsTheme.metricError : undefined,
      checked: value === "Dismissed",
    },
  ];

  const reportAction = {
    tooltipKey: "media.analytics.insights.reportInaccurate",
    icon: <FlagIcon size={compact ? 14 : 20} />,
    color: colors.status.warning,
    handler: onReport,
    backgroundColor: compact ? undefined : colors.status.warningBg,
    label: t("media.analytics.insights.reportInaccurate"),
  };

  const buttonStyle: CSSProperties = useMemo(() => {
    if (compact) return {};
    return {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexDirection: "column",
      padding: "18px 12px",
      height: 80,
      flex: "30%",
    };
  }, [compact]);

  return (
    <Flex gap={16} vertical>
      <Flex gap={6}>
        {actions.map(
          ({
            tooltipKey,
            icon,
            color,
            handler,
            label,
            disabled,
            backgroundColor,
            checked,
          }) => {
            if (disabled) return null;
            return (
              <Tooltip key={tooltipKey} title={t(tooltipKey)}>
                <Button
                  size="middle"
                  shape={compact ? "circle" : "default"}
                  onClick={(e) => {
                    e.stopPropagation();
                    handler?.(insightId);
                  }}
                  style={{
                    color: checked ? "white" : color,
                    borderColor: checked ? backgroundColor : color,
                    backgroundColor,
                    ...buttonStyle,
                  }}
                >
                  {icon}
                  {!compact && <span>{label}</span>}
                </Button>
              </Tooltip>
            );
          }
        )}
      </Flex>

      {onReport && (
        <Tooltip title={t(reportAction.tooltipKey)}>
          <Button
            size="middle"
            shape={compact ? "circle" : "default"}
            onClick={(e) => {
              e.stopPropagation();
              onReport(insightId);
            }}
            style={{
              alignSelf: "flex-start",
              borderColor: reportAction.color,
              backgroundColor: reportAction.backgroundColor,
              color: reportAction.color,
            }}
          >
            {reportAction.icon}
            {!compact && <span>{reportAction.label}</span>}
          </Button>
        </Tooltip>
      )}
    </Flex>
  );
};
